// Trading-Store — Zustand mit localStorage-Persist. V1-Pendant: src/hooks/useTradingLog.js
// In V1 wurde direkt auf `localStorage['eve_trading_log_v1']` gelesen/geschrieben.
// V3 nutzt Zustand-`persist`-Middleware mit eigenem Storage-Key — Verhalten gleich,
// aber React-State-Subscriptions inklusive.
//
// Storage-Key bleibt absichtlich auf V1-Pattern: `eve_trading_log_v3`.
// Beim Cutover wird via Migrations-Step der V1-Key gelesen falls vorhanden.

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import {
  MAX_TRADES,
  closeTradeRecord,
  computeStats,
  createTradeRecord,
  filterClosed,
  filterOpen,
  formatTradesForPrompt,
} from '@/lib/trade-log';
import type { CloseTradeInput, CreateTradeInput, Trade, TradingStats } from '@/types/trade';

export const TRADING_STORE_KEY = 'eve_trading_log_v3';

export interface TradingState {
  trades: Trade[];
}

export interface TradingActions {
  /** Erzeugt einen neuen Trade, prependet ihn und schneidet auf MAX_TRADES. */
  createTrade: (input: CreateTradeInput) => Trade;
  /**
   * Schließt einen Trade. Gibt `true` zurück wenn der Trade offen war und geschlossen
   * wurde, `false` wenn er nicht offen war (analog V1 `wasClosed`-Boolean).
   */
  closeTrade: (id: string, input: CloseTradeInput) => boolean;
  updateNotes: (id: string, notes: string) => void;
  deleteTrade: (id: string) => void;
  clearAll: () => void;
  /** Setzt den Store auf einen exakten Trades-Array — für Tests und D1-Sync (Phase 2). */
  hydrate: (trades: Trade[]) => void;
}

export type TradingStore = TradingState & TradingActions;

export const useTradingStore = create<TradingStore>()(
  persist(
    (set, get) => ({
      trades: [],

      createTrade: (input) => {
        const trade = createTradeRecord(input);
        set((state) => ({ trades: [trade, ...state.trades].slice(0, MAX_TRADES) }));
        return trade;
      },

      closeTrade: (id, input) => {
        let wasClosed = false;
        set((state) => ({
          trades: state.trades.map((t) => {
            if (t.id !== id) return t;
            const updated = closeTradeRecord(t, input);
            if (updated === null) return t;
            wasClosed = true;
            return updated;
          }),
        }));
        return wasClosed;
      },

      updateNotes: (id, notes) => {
        set((state) => ({
          trades: state.trades.map((t) => (t.id === id ? { ...t, notes } : t)),
        }));
      },

      deleteTrade: (id) => {
        set((state) => ({ trades: state.trades.filter((t) => t.id !== id) }));
      },

      clearAll: () => {
        set({ trades: [] });
      },

      hydrate: (trades) => {
        set({ trades: trades.slice(0, MAX_TRADES) });
        return get();
      },
    }),
    {
      name: TRADING_STORE_KEY,
      storage: createJSONStorage(() => localStorage),
      // Nur trades persistieren — keine Functions
      partialize: (state) => ({ trades: state.trades }),
      version: 1,
    },
  ),
);

// ── Selektoren ───────────────────────────────────────────────────────────────

export const selectAllTrades = (state: TradingState): Trade[] => state.trades;
export const selectOpenTrades = (state: TradingState): Trade[] => filterOpen(state.trades);
export const selectClosedTrades = (state: TradingState): Trade[] => filterClosed(state.trades);
export const selectTradingStats = (state: TradingState): TradingStats | null =>
  computeStats(state.trades);
export const selectTradeById =
  (id: string) =>
  (state: TradingState): Trade | undefined =>
    state.trades.find((t) => t.id === id);
export const selectFormattedForPrompt = (state: TradingState): string | null =>
  formatTradesForPrompt(state.trades);
